import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import EmojiPicker from '@emoji-mart/react';
import emojiData from '@emoji-mart/data';
import { GlobalContext } from '../../context/GlobalContext';
import { useChat } from '../../context/ChatContext';
import haxios from '../../lib/haxios';
import { errorToast } from '../../lib/helpers';
import { Close } from '../../design/Icons';
import * as chat from '../../lib/chatApi';
import '../../design/chat.css';

// The single, app-wide chat/comments modal. Pointed at one entity at a time by
// ChatContext. One permanent thread per entity — load it, post into it, react,
// reply (one level), edit/delete your own. Polls every 12s while open.

const POLL_MS = 12000;

const initialsFrom = (name, inits) =>
  inits || (name || '-').trim().split(/\s+/).slice(0, 2).map(p => p[0]).join('').toUpperCase();

const relTime = (iso) => {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 45)    return 'just now';
  if (diff < 3600)  return `${Math.round(diff / 60)}m`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h`;
  return `${Math.round(diff / 86400)}d`;
};
const absTime = (iso) => iso ? new Date(iso).toLocaleString('en-GB', {
  weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
}) : '';

const snippet = (s, n = 60) => {
  const t = (s || '').replace(/\s+/g, ' ').trim();
  return t.length > n ? `${t.slice(0, n)}…` : t;
};

// Header label per entity type. The three private / participant-gated threads
// (feedback, follow-up, request) need their own names — otherwise they'd all
// read as "Customer".
const KIND_LABELS = {
  customer:      'Customer',
  bdm_visit:     'BDM Visit',
  bdm_feedback:  'Feedback',
  follow_up:     'Follow-up',
  visit_request: 'Visit request',
};

const ChatModal = () => {
  const { target, closeChat } = useChat();
  if (!target) return null;
  // key resets all internal state when the entity changes.
  return <ChatPane key={`${target.entityType}:${target.entityId}`} target={target} onClose={closeChat} />;
};

// Modal chrome around the shared thread. Count/label for the header are fed
// back up from ChatThread via onMeta, since the thread owns the fetch.
const ChatPane = ({ target, onClose }) => {
  const { entityType, entityId, label } = target;
  const [meta, setMeta] = useState({ count: 0, label: label || entityId });
  const kindLabel = KIND_LABELS[entityType] || 'Thread';
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card chat-card" onClick={e => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <div className="label">Comments · {kindLabel}</div>
            <div className="modal-title">{meta.label}</div>
            <div className="label num" style={{ marginTop: 2 }}>
              {meta.count} {meta.count === 1 ? 'comment' : 'comments'} · {entityId}
            </div>
          </div>
          <button type="button" className="btn ghost sm" onClick={onClose}><Close size={12}/></button>
        </div>
        <ChatThread entityType={entityType} entityId={entityId} label={label} onClose={onClose} onMeta={setMeta} />
      </div>
    </div>
  );
};

// The thread + composer, embeddable anywhere — the modal above, or inline
// (e.g. the Feedback detail page's Discussion panel). The host container must
// be a column flexbox with a height constraint: .chat-thread is flex:1 and
// scrolls internally.
//   onClose  optional — when provided, Esc closes (modal use only)
//   onMeta   optional — receives { count, label } so the host can render chrome
export const ChatThread = ({ entityType, entityId, label, onClose, onMeta }) => {
  const { uinfo } = useContext(GlobalContext);

  const [thread, setThread]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [draft, setDraft]     = useState('');
  const [replyTo, setReplyTo] = useState(null);   // parent message id
  const [busy, setBusy]       = useState(false);
  const [editingId, setEditingId]     = useState(null);
  const [editingText, setEditingText] = useState('');
  const [pickerFor, setPickerFor]     = useState(null); // message id for emoji picker

  // @mention autocomplete
  const [operators, setOperators] = useState([]);
  const [mention, setMention]     = useState({ open: false, query: '' });
  const [mentionIdx, setMentionIdx] = useState(0);

  const threadRef = useRef(null);
  const taRef     = useRef(null);
  const lastCount = useRef(0);

  // Read-receipt reporting. As other people's messages scroll into view we
  // batch their ids and POST /chat/read-receipts, so the author's "seen by"
  // list stays truthful. reportedRef guards against double-posting; pendingRef
  // buffers ids between debounced flushes.
  const observerRef = useRef(null);
  const reportedRef = useRef(new Set());
  const pendingRef  = useRef(new Set());
  const flushTimer  = useRef(null);

  const load = useCallback(async ({ silent } = {}) => {
    if (!silent) setLoading(true);
    try {
      const data = await chat.fetchThread(entityType, entityId);
      setThread(data);
      setError(null);
    } catch (err) {
      const s = err?.response?.status;
      if (s === 404) setError('not_found');
      // 403 → the caller isn't a participant on this gated thread. Per the
      // comms brief, don't surface a raw error — show a "you're not in this
      // loop" panel guiding them to be added.
      else if (s === 403) setError('forbidden');
      else if (!silent) errorToast('Could not load comments');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [entityType, entityId]);

  useEffect(() => { load(); }, [load]);

  // Poll while open + refetch on window focus. Skip while the user is mid-send
  // or editing so we don't clobber optimistic / in-progress state.
  useEffect(() => {
    const tick = () => { if (!busy && editingId == null) load({ silent: true }); };
    const t = setInterval(tick, POLL_MS);
    window.addEventListener('focus', tick);
    return () => { clearInterval(t); window.removeEventListener('focus', tick); };
  }, [load, busy, editingId]);

  // Esc closes — modal hosting only (inline embeds pass no onClose).
  useEffect(() => {
    if (!onClose) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !mention.open && !pickerFor) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, mention.open, pickerFor]);

  // Auto-scroll to newest when the count grows.
  useEffect(() => {
    const n = thread?.messages?.length || 0;
    if (threadRef.current && n > lastCount.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
    lastCount.current = n;
  }, [thread]);

  // ---- read receipts ----------------------------------------------------

  const flushReads = useCallback(() => {
    const ids = Array.from(pendingRef.current).filter(id => typeof id === 'number');
    pendingRef.current.clear();
    if (!ids.length) return;
    ids.forEach(id => reportedRef.current.add(id));
    chat.reportReads(ids)
      .then(() => {
        // Reflect our own read locally so the message no longer looks unread.
        setThread(t => t ? {
          ...t,
          messages: t.messages.map(m => ids.includes(m.id) ? { ...m, readBySelf: true } : m),
        } : t);
      })
      .catch(() => {
        // Let a future scroll retry these (endpoint may 404 on older BE).
        ids.forEach(id => reportedRef.current.delete(id));
      });
  }, []);

  const scheduleFlush = useCallback(() => {
    if (flushTimer.current) return;
    flushTimer.current = setTimeout(() => { flushTimer.current = null; flushReads(); }, 600);
  }, [flushReads]);

  // One IntersectionObserver for the thread viewport. Messages tag themselves
  // with data-mid only when they still need a receipt (someone else's, unread,
  // real id) — see renderMessage — so anything observed here is worth reporting.
  useEffect(() => {
    const root = threadRef.current;
    if (!root || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver((entries) => {
      let added = false;
      for (const e of entries) {
        if (!e.isIntersecting) continue;
        const id = Number(e.target.getAttribute('data-mid'));
        io.unobserve(e.target);
        if (!id || reportedRef.current.has(id)) continue;
        pendingRef.current.add(id);
        added = true;
      }
      if (added) scheduleFlush();
    }, { root, threshold: 0.6 });
    observerRef.current = io;
    return () => { io.disconnect(); observerRef.current = null; };
  }, [scheduleFlush]);

  // Observe freshly-rendered message nodes whenever the thread changes.
  useEffect(() => {
    const io = observerRef.current;
    const root = threadRef.current;
    if (!io || !root) return;
    root.querySelectorAll('[data-mid]').forEach(node => {
      const id = Number(node.getAttribute('data-mid'));
      if (id && !reportedRef.current.has(id)) io.observe(node);
    });
  }, [thread]);

  useEffect(() => () => { if (flushTimer.current) clearTimeout(flushTimer.current); }, []);

  // Build a flat→threaded view: top-level messages + replies grouped under
  // their top-level ancestor (one visual level, per spec §4.3).
  const { topLevel, repliesByParent, byId } = useMemo(() => {
    const msgs = thread?.messages || [];
    const map = new Map(msgs.map(m => [m.id, m]));
    const ancestorOf = (m) => {
      let cur = m; const seen = new Set();
      while (cur.parentId != null && map.has(cur.parentId) && !seen.has(cur.id)) {
        seen.add(cur.id); cur = map.get(cur.parentId);
      }
      return cur;
    };
    const tops = [], replies = new Map();
    for (const m of msgs) {
      if (m.parentId == null) tops.push(m);
      else {
        const anc = ancestorOf(m);
        const arr = replies.get(anc.id) || [];
        arr.push(m); replies.set(anc.id, arr);
      }
    }
    return { topLevel: tops, repliesByParent: replies, byId: map };
  }, [thread]);

  // ---- actions ----------------------------------------------------------

  const send = async () => {
    const body = draft.trim();
    if (!body || busy) return;
    setBusy(true);
    const keepReply = replyTo;
    const optimistic = {
      id: `temp-${Date.now()}`, parentId: keepReply || null, body,
      deleted: false, createdAt: new Date().toISOString(), editedAt: null,
      author: { inits: uinfo?.Inits, name: uinfo?.Name || uinfo?.Inits, isSelf: true },
      reactions: [], pending: true,
    };
    setThread(t => ({ ...t, messages: [...(t?.messages || []), optimistic] }));
    setDraft(''); setReplyTo(null); setMention({ open: false, query: '' });
    try {
      const created = await chat.postMessage(entityType, entityId, { body, parentId: keepReply || null });
      setThread(t => ({
        ...t,
        messageCount: (t.messageCount || 0) + 1,
        messages: t.messages.map(m => m.id === optimistic.id
          ? { ...created, author: { ...(created.author || {}), isSelf: true } }
          : m),
      }));
    } catch (err) {
      setThread(t => ({ ...t, messages: t.messages.filter(m => m.id !== optimistic.id) }));
      setDraft(body); setReplyTo(keepReply || null);
      const d = err?.response?.data;
      errorToast(d?.detail || d?.error || 'Failed to post');
    } finally {
      setBusy(false);
    }
  };

  const react = async (messageId, emoji) => {
    setPickerFor(null);
    try {
      const res = await chat.toggleReaction(messageId, emoji);
      setThread(t => ({
        ...t,
        messages: t.messages.map(m => {
          if (m.id !== messageId) return m;
          const others = (m.reactions || []).filter(r => r.emoji !== res.emoji);
          const next = res.count > 0
            ? [...others, { emoji: res.emoji, count: res.count, reactedBySelf: res.reactedBySelf, users: res.users }]
            : others;
          return { ...m, reactions: next };
        }),
      }));
    } catch (err) {
      const s = err?.response?.status;
      if (s === 409 || s === 404) { errorToast('Message changed - refreshing'); load({ silent: true }); }
      else errorToast('Could not react');
    }
  };

  const startEdit = (m) => { setEditingId(m.id); setEditingText(m.body || ''); };
  const saveEdit = async (id) => {
    const body = editingText.trim();
    if (!body) return;
    try {
      const updated = await chat.editMessage(id, body);
      setThread(t => ({
        ...t,
        messages: t.messages.map(m => m.id === id
          ? { ...updated, author: { ...(updated.author || {}), isSelf: m.author?.isSelf } }
          : m),
      }));
      setEditingId(null); setEditingText('');
    } catch (err) {
      if (err?.response?.status === 403) errorToast('You can only edit your own messages');
      else errorToast('Edit failed');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this comment? Replies under it are kept.')) return;
    try {
      await chat.deleteMessage(id);
      setThread(t => ({
        ...t,
        messages: t.messages.map(m => m.id === id ? { ...m, deleted: true, body: null, reactions: [] } : m),
      }));
    } catch (err) {
      if (err?.response?.status === 403) errorToast('You can only delete your own messages');
      else errorToast('Delete failed');
    }
  };

  // ---- @mention ---------------------------------------------------------

  const loadOperators = () => {
    if (operators.length) return;
    haxios.get('/salesassist/cplusassist/getOperators')
      .then(r => {
        const list = Array.isArray(r) ? r : (r?.operators || []);
        setOperators(list
          .map(o => ({ inits: o.Inits || o.inits, name: o.Name || o.name || '' }))
          .filter(o => o.inits));
      })
      .catch(() => {});
  };

  const mentionList = useMemo(() => {
    if (!mention.open) return [];
    const q = mention.query.toLowerCase();
    return operators
      .filter(o => o.inits.toLowerCase().includes(q) || (o.name || '').toLowerCase().includes(q))
      .slice(0, 6);
  }, [mention, operators]);

  const onDraftChange = (e) => {
    const val = e.target.value;
    setDraft(val);
    const caret = e.target.selectionStart ?? val.length;
    const m = /(^|\s)@(\w*)$/.exec(val.slice(0, caret));
    if (m) { setMention({ open: true, query: m[2] }); setMentionIdx(0); }
    else setMention({ open: false, query: '' });
  };

  const pickMention = (o) => {
    const caret = taRef.current?.selectionStart ?? draft.length;
    const before = draft.slice(0, caret).replace(/@(\w*)$/, `@${o.inits} `);
    const after = draft.slice(caret);
    setDraft(before + after);
    setMention({ open: false, query: '' });
    requestAnimationFrame(() => {
      if (!taRef.current) return;
      taRef.current.focus();
      taRef.current.setSelectionRange(before.length, before.length);
    });
  };

  const onComposerKey = (e) => {
    if (mention.open && mentionList.length) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setMentionIdx(i => (i + 1) % mentionList.length); return; }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setMentionIdx(i => (i - 1 + mentionList.length) % mentionList.length); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); pickMention(mentionList[mentionIdx]); return; }
      if (e.key === 'Escape') { setMention({ open: false, query: '' }); return; }
    }
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  // ---- render helpers ---------------------------------------------------

  const headerLabel = thread?.entityLabel || label || entityId;
  const count = thread?.messageCount ?? (thread?.messages || []).filter(m => !m.deleted).length;
  const kindLabel = KIND_LABELS[entityType] || 'Thread';

  // Feed count/label up to whatever chrome hosts us (modal header, panel pill).
  useEffect(() => { onMeta?.({ count, label: headerLabel }); }, [onMeta, count, headerLabel]);

  const renderMessage = (m, isReply) => {
    const isSelf = m.author?.isSelf;
    const inits = initialsFrom(m.author?.name, m.author?.inits);
    const who = m.author?.name || m.author?.inits || '-';
    const parent = m.parentId != null ? byId.get(m.parentId) : null;
    const editing = editingId === m.id;

    // Only real, someone-else's, still-unread messages carry data-mid — those
    // are exactly the ones the IntersectionObserver should report a read for.
    const needsReceipt = !isSelf && !m.deleted && typeof m.id === 'number' && !m.readBySelf;

    // "Seen by" — from the summarised readBy[] the thread payload carries.
    // The author is excluded server-side, so this reads as "who else has seen it".
    const seen = Array.isArray(m.readBy) ? m.readBy : [];
    const seenCount = m.readCount != null ? m.readCount : seen.length;
    const seenNames = seen.map(u => u.name || u.inits).filter(Boolean);
    const seenLabel = seenNames.length
      ? `Seen by ${seenNames.slice(0, 3).join(', ')}${seenCount > 3 ? ` +${seenCount - 3}` : ''}`
      : `Seen by ${seenCount}`;

    return (
      <div
        key={m.id}
        className={`chat-msg ${isReply ? 'reply' : ''} ${isSelf ? 'self' : ''}`}
        {...(needsReceipt ? { 'data-mid': m.id } : {})}
      >
        <div className="chat-avatar" title={who}>{inits}</div>
        <div className="chat-msg-main">
          <div className="chat-msg-meta">
            <span className="chat-author">{who}</span>
            <span className="chat-time num" title={absTime(m.createdAt)}>{relTime(m.createdAt)}</span>
            {m.editedAt && !m.deleted && <span className="chat-edited label">(edited)</span>}
            {m.pending && <span className="label" style={{ color: 'var(--ink-4)' }}>sending…</span>}
          </div>

          {/* reply context quote */}
          {isReply && parent && (
            <div className="chat-quote">
              ↳ {parent.author?.name || parent.author?.inits || '-'}: {parent.deleted ? 'deleted message' : snippet(parent.body)}
            </div>
          )}

          {m.deleted ? (
            <div className="chat-body deleted">This message was deleted</div>
          ) : editing ? (
            <div className="chat-edit">
              <textarea
                className="chat-edit-input"
                value={editingText}
                onChange={e => setEditingText(e.target.value)}
                rows={2}
                autoFocus
              />
              <div className="chat-edit-actions">
                <button type="button" className="btn ghost sm" onClick={() => { setEditingId(null); setEditingText(''); }}>Cancel</button>
                <button type="button" className="btn primary sm" onClick={() => saveEdit(m.id)} disabled={!editingText.trim()}>Save</button>
              </div>
            </div>
          ) : (
            <div className="chat-body">{m.body}</div>
          )}

          {/* reactions */}
          {!m.deleted && (
            <div className="chat-reactions">
              {(m.reactions || []).map(r => (
                <button
                  key={r.emoji}
                  type="button"
                  className={`chat-pill ${r.reactedBySelf ? 'mine' : ''}`}
                  onClick={() => react(m.id, r.emoji)}
                  title={`${(r.users || []).map(u => u.name || u.inits).join(', ')} reacted with ${r.emoji}`}
                >
                  <span className="chat-pill-emoji">{r.emoji}</span>
                  <span className="num">{r.count}</span>
                </button>
              ))}
              {!m.pending && (
                <span className="chat-react-wrap">
                  <button
                    type="button"
                    className="chat-pill add"
                    onClick={() => setPickerFor(pickerFor === m.id ? null : m.id)}
                    title="Add reaction"
                  >
                    ☺ +
                  </button>
                  {pickerFor === m.id && (
                    <>
                      <div className="chat-picker-backdrop" onClick={() => setPickerFor(null)} />
                      <div className="chat-picker-pop">
                        <EmojiPicker
                          data={emojiData}
                          onEmojiSelect={(e) => react(m.id, e.native)}
                          theme="light"
                          previewPosition="none"
                          skinTonePosition="none"
                          maxFrequentRows={1}
                        />
                      </div>
                    </>
                  )}
                </span>
              )}

              {/* row actions */}
              {!m.pending && !editing && (
                <span className="chat-actions">
                  <button type="button" className="chat-action" onClick={() => { setReplyTo(m.parentId ?? m.id); taRef.current?.focus(); }}>Reply</button>
                  {isSelf && <button type="button" className="chat-action" onClick={() => startEdit(m)}>Edit</button>}
                  {isSelf && <button type="button" className="chat-action danger" onClick={() => remove(m.id)}>Delete</button>}
                </span>
              )}
            </div>
          )}

          {/* seen-by receipt */}
          {!m.deleted && seenCount > 0 && (
            <div
              className="chat-seen label"
              title={seen.map(u => `${u.name || u.inits}${u.at ? ` · ${absTime(u.at)}` : ''}`).join('\n')}
            >
              ✓ {seenLabel}
            </div>
          )}

          {/* replies */}
          {!isReply && (repliesByParent.get(m.id) || []).length > 0 && (
            <div className="chat-replies">
              {(repliesByParent.get(m.id) || []).map(r => renderMessage(r, true))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const replyParent = replyTo != null ? byId.get(replyTo) : null;

  return (
    <>
      <div className="chat-thread" ref={threadRef}>
          {loading && <div className="chat-empty label">LOADING COMMENTS…</div>}
          {!loading && error === 'not_found' && (
            <div className="chat-empty label">This {kindLabel.toLowerCase()} could not be found.</div>
          )}
          {!loading && error === 'forbidden' && (
            <div className="chat-gate">
              <div className="chat-gate-title">You're not in this conversation.</div>
              <div className="chat-gate-body label">
                This thread is private to the people looped in on the
                {' '}{kindLabel.toLowerCase()}. Ask an admin, or anyone already
                on it, to add you as a participant - then it'll open here.
              </div>
            </div>
          )}
          {!loading && !error && topLevel.length === 0 && (
            <div className="chat-empty label">No comments yet. Start the conversation.</div>
          )}
          {!loading && !error && topLevel.map(m => renderMessage(m, false))}
        </div>

        {!error && (
          <div className="chat-composer">
            {replyParent && (
              <div className="chat-replying">
                <span>Replying to <strong>{replyParent.author?.name || replyParent.author?.inits}</strong>: {snippet(replyParent.body, 40)}</span>
                <button type="button" className="chat-action" onClick={() => setReplyTo(null)}>Cancel</button>
              </div>
            )}
            <div className="chat-composer-row">
              {mention.open && mentionList.length > 0 && (
                <div className="chat-mention-pop">
                  {mentionList.map((o, i) => (
                    <button
                      key={o.inits}
                      type="button"
                      className={`chat-mention-item ${i === mentionIdx ? 'on' : ''}`}
                      onMouseDown={(e) => { e.preventDefault(); pickMention(o); }}
                    >
                      <span className="num chat-mention-inits">{o.inits}</span>
                      <span className="chat-mention-name">{o.name}</span>
                    </button>
                  ))}
                </div>
              )}
              <textarea
                ref={taRef}
                className="chat-input"
                placeholder={`Comment on ${headerLabel}…  (@ to mention, Enter to send)`}
                value={draft}
                onChange={onDraftChange}
                onKeyDown={onComposerKey}
                onFocus={loadOperators}
                rows={2}
              />
              <button type="button" className="btn orange chat-send" onClick={send} disabled={!draft.trim() || busy}>
                Send
              </button>
            </div>
          </div>
        )}
    </>
  );
};

export default ChatModal;
